import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import SkeletonTable from "../../Skeleton/SkeletonTable";
import cdpService from '../../../apiService/cdp.service';

const getCustomerId = (customer) => customer?.id ?? customer?.sso_id;

const CustomerListTable = ({ filtersdata }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [customers, setCustomers] = useState([]);
    const [resultMessage, setResultMessage] = useState('');
    const [prevFilters, setPrevFilters] = useState(filtersdata);
    const searchTerm = filtersdata?.searchTerm?.trim() || '';

    if (filtersdata !== prevFilters) {
        setPrevFilters(filtersdata);
        setCurrentPage(1);
        setCustomers([]);
        setTotalPages(1);
        setResultMessage('');
    }

    useEffect(() => {
        if (!searchTerm) return undefined;

        const controller = new AbortController();
        let active = true;

        async function fetchData() {
            setLoading(true);
            try {
                const response = await cdpService.getCustomerReportList({
                    searchBy: filtersdata?.searchBy || 'id',
                    search: searchTerm,
                    page: currentPage,
                    length: perPage,
                }, { signal: controller.signal });
                if (!active) return;
                const body = response.data;
                if (!body?.success) {
                    setCustomers([]);
                    setTotalPages(1);
                    setResultMessage(body?.message || 'Unable to load customers.');
                    return;
                }
                const reportData = body.data?.reportData || {};
                const dataList = Array.isArray(reportData.dataList) ? reportData.dataList : [];
                setResultMessage('');
                setCustomers(dataList);
                setTotalPages(reportData.totalPages > 0 ? reportData.totalPages : 1);
            } catch (error) {
                if (!active || controller.signal.aborted) return;
                setCustomers([]);
                setTotalPages(1);
                setResultMessage(error.response?.data?.message || 'Unable to load customers.');
            } finally {
                if (active) setLoading(false);
            }
        }

        fetchData();
        return () => {
            active = false;
            controller.abort();
        };
    }, [currentPage, perPage, filtersdata, searchTerm]);

    return (
        <div className="bg-white rounded-lg shadow-sm">
            <div className="w-full px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center mb-3 md:mb-0">
                    <label className="text-gray-600 text-sm">Show</label>
                    <select
                        className="mx-3 rounded-md border border-gray-300 py-1.5 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={perPage}
                        onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        {[10, 25, 50, 100].map((num) => (
                            <option key={num} value={num}>{num}</option>
                        ))}
                    </select>
                    <label className="text-gray-600 text-sm">entries</label>
                </div>
            </div>
            <div className="overflow-x-auto">
                {loading ? (
                    <SkeletonTable rowCount={10} columnCount={8} />
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SSO ID</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile No.</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gender</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {customers.length === 0 && (
                                <tr>
                                    <td className="px-4 py-4 text-center text-sm text-gray-500" colSpan={8}>
                                        {resultMessage
                                            ? resultMessage
                                            : searchTerm
                                                ? 'No customers found'
                                                : 'Enter an SSO ID, email, or mobile number and click Apply to search.'}
                                    </td>
                                </tr>
                            )}
                            {customers.map((customer, index) => (
                                <tr
                                    key={`${currentPage}-${customer.serial_no ?? index}`}
                                    className={getCustomerId(customer) ? "cursor-pointer hover:bg-gray-50" : "hover:bg-gray-50"}
                                    role={getCustomerId(customer) ? "link" : undefined}
                                    tabIndex={getCustomerId(customer) ? 0 : undefined}
                                    onClick={() => {
                                        const customerId = getCustomerId(customer);
                                        if (customerId) navigate(`/cdp/customers/${customerId}`);
                                    }}
                                    onKeyDown={(event) => {
                                        const customerId = getCustomerId(customer);
                                        if (customerId && event.key === "Enter") {
                                            navigate(`/cdp/customers/${customerId}`);
                                        }
                                    }}
                                >
                                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">{customer.serial_no ?? "-"}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{getCustomerId(customer) ?? "-"}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{customer.name || "-"}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{customer.mobile_no || "-"}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{customer.email || "-"}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{customer.gender || "-"}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{customer.age ?? "-"}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{customer.product || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center space-x-2">
                        <button
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={currentPage === 1 || loading}
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                        <button
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={currentPage === totalPages || loading}
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerListTable;
